import { ArrowDown, ArrowUp, ArrowUpDown } from "lucide-react";
import StatusBadge from "@/components/StatusBadge";
import { fmtDate, fmtDateTime, money, PRICE_LABEL, deviceLabel } from "@/lib/auditDates";

const COLS = [
  { key: "order_number", label: "Orden", sort: "order_number" },
  { key: "customer", label: "Cliente" },
  { key: "device", label: "Equipo / modelo" },
  { key: "services", label: "Servicio realizado" },
  { key: "branch", label: "Sucursal" },
  { key: "technician", label: "Técnico", sort: "technician" },
  { key: "received", label: "Recepción", sort: "received" },
  { key: "finalized", label: "Finalización", sort: "finalized" },
  { key: "pickup", label: "Retiro", sort: "pickup" },
  { key: "status", label: "Estado actual" },
  { key: "price", label: "Precio final", sort: "price", right: true },
];

const SortIcon = ({ active, dir }) =>
  !active ? <ArrowUpDown size={12} className="opacity-40" /> : dir === "asc" ? <ArrowUp size={12} /> : <ArrowDown size={12} />;

const PriceCell = ({ r }) => (
  <div className="text-right">
    <div className={`font-mono font-semibold ${r.price_kind === "final" ? "text-slate-900" : "text-slate-500"}`}>
      {r.price != null ? money(r.price) : "—"}
    </div>
    <div className="text-[10px] text-slate-400 whitespace-nowrap">{r.device_count > 1 && (r.price_kind === "final" || r.price_kind === "approved") ? "Precio del equipo" : PRICE_LABEL[r.price_kind]}{r.device_count > 1 && r.price_kind === "budget" && " (equipo)"}</div>
  </div>
);

export const OrdersTable = ({ rows, sort, dir, onSort, onOpen }) => (
  <div className="card-base overflow-x-auto" data-testid="orders-table">
    <table className="w-full min-w-[1150px] text-sm">
      <thead className="bg-slate-50 border-b border-slate-200 sticky top-0">
        <tr>
          {COLS.map((c) => (
            <th key={c.key} className={`px-3 py-2.5 text-[11px] font-semibold uppercase tracking-wide text-slate-500 ${c.right ? "text-right" : "text-left"}`}>
              {c.sort ? (
                <button type="button" onClick={() => onSort(c.sort)} className="inline-flex items-center gap-1 hover:text-slate-900" data-testid={`orders-table-sort-${c.sort}`}>
                  {c.label} <SortIcon active={sort === c.sort} dir={dir} />
                </button>
              ) : c.label}
            </th>
          ))}
        </tr>
      </thead>
      <tbody className="divide-y divide-slate-100">
        {rows.map((r, i) => {
          const multi = r.device_count > 1;
          const first = i === 0 || rows[i - 1].order_id !== r.order_id;
          return (
          <tr key={r.row_id} onClick={() => onOpen(r)}
            className={`cursor-pointer hover:bg-blue-50/60 transition-colors ${multi ? "bg-indigo-50/30" : ""}`}
            data-testid={`orders-table-row-${r.order_number}-${r.device_index}`}>
            <td className={`px-3 py-2 whitespace-nowrap ${multi ? "border-l-4 border-indigo-400" : ""}`}>
              {(!multi || first) ? <div className="font-mono text-xs text-blue-600">{r.order_number}</div> : <div className="font-mono text-[10px] text-slate-400 pl-2">↳ {r.order_number}</div>}
              {multi && <div className="text-[10px] font-semibold text-indigo-600">Equipo {r.device_index} de {r.device_count}</div>}
              {multi && first && <div className="text-[10px] font-semibold text-slate-700" data-testid={`orders-table-order-total-${r.order_number}`}>Total orden: {money(r.order_total)}</div>}
            </td>
            <td className="px-3 py-2 max-w-[160px] truncate font-medium">{r.customer_name || "—"}</td>
            <td className="px-3 py-2 max-w-[170px] truncate text-slate-600">{deviceLabel(r) || "—"}</td>
            <td className="px-3 py-2 max-w-[200px] truncate text-slate-600" title={r.services}>{r.services || "—"}</td>
            <td className="px-3 py-2 whitespace-nowrap text-slate-600">{r.branch_name || "—"}</td>
            <td className="px-3 py-2 whitespace-nowrap">{r.technician_name || <span className="text-slate-400">Sin asignar</span>}</td>
            <td className="px-3 py-2 whitespace-nowrap font-mono text-xs">{fmtDate(r.received_at)}</td>
            <td className="px-3 py-2 whitespace-nowrap font-mono text-xs">{fmtDateTime(r.finalized_at)}</td>
            <td className="px-3 py-2 whitespace-nowrap font-mono text-xs">{fmtDateTime(r.pickup_at)}</td>
            <td className="px-3 py-2 whitespace-nowrap"><StatusBadge status={r.status} /></td>
            <td className="px-3 py-2"><PriceCell r={r} /></td>
          </tr>
          );
        })}
      </tbody>
    </table>
  </div>
);
